import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { ChevronLeft, Plus, Trash2, AlertCircle, RefreshCw } from 'lucide-react';

interface BusinessProfile {
  id: string;
  name: string;
}

interface Application {
  id: string;
  name: string;
}

interface AccountLite {
  id: string;
  name: string;
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  sortOrder: number;
  itemType: string;
}

const emptyItem: LineItem = {
  description: '',
  quantity: 1,
  unitPrice: 0,
  sortOrder: 0,
  itemType: 'service',
};

// Convert API date values (which may come back as ISO timestamps from SQL Server)
// into the YYYY-MM-DD format that <input type="date"> requires.
function toDateInputValue(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    return value.slice(0, 10);
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
}

export function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [businessProfileId, setBusinessProfileId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Net 7');
  const [items, setItems] = useState<LineItem[]>([{ ...emptyItem }]);

  // Load data for dropdowns
  const { data: profiles } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: async () => {
      const result = await api.getBusinessProfiles();
      return (result.data || []) as BusinessProfile[];
    },
  });

  const { data: accounts } = useQuery({
    queryKey: ['invoice-accounts'],
    queryFn: async () => {
      const result = await api.getInvoiceAccounts();
      return (result.data || []) as AccountLite[];
    },
  });

  const { data: applications } = useQuery({
    queryKey: ['finance-applications'],
    queryFn: async () => {
      const result = await api.getFinanceApplications();
      return (result.data || []) as Application[];
    },
  });

  // Load existing invoice if editing
  const {
    data: existingInvoice,
    isLoading: invoiceLoading,
    isError: invoiceLoadFailed,
    error: invoiceLoadError,
    refetch: refetchInvoice,
    isFetching: invoiceRefetching,
  } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await api.getInvoice(id);
      // api.request() throws on HTTP errors but returns a body on network
      // failure, so an absent payload has to be turned into a real error —
      // otherwise the query resolves with undefined and the form sits on a
      // spinner forever with nothing to show the user.
      if (!result.data) {
        throw new Error(result.error?.message || 'The invoice could not be loaded.');
      }
      return result.data as Record<string, unknown>;
    },
    enabled: isEditing,
    retry: 1,
  });

  // Populate form when editing as soon as the invoice itself loads.
  // We no longer wait on profiles/accounts/applications because the merged
  // *Options memos below ensure the Select can always render the existing value.
  useEffect(() => {
    if (existingInvoice) {
      setBusinessProfileId((existingInvoice.businessProfileId as string) || '');
      setAccountId((existingInvoice.accountId as string) || '');
      setApplicationId((existingInvoice.applicationId as string) || '');
      setIssueDate(toDateInputValue(existingInvoice.issueDate));
      setDueDate(toDateInputValue(existingInvoice.dueDate));
      setTaxRate((existingInvoice.taxRate as number) || 0);
      setNotes((existingInvoice.notes as string) || '');
      setPaymentTerms((existingInvoice.paymentTerms as string) || 'Net 7');
      const existingItems = existingInvoice.items as LineItem[] | undefined;
      if (existingItems && existingItems.length > 0) {
        setItems(existingItems.map((item, i) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          sortOrder: i,
          itemType: item.itemType || 'service',
        })));
      }
    }
  }, [existingInvoice, profiles, accounts, applications]);

  // Set default due date based on payment terms
  useEffect(() => {
    if (!dueDate && issueDate) {
      const issue = new Date(issueDate);
      const days = paymentTerms === 'Net 7' ? 7 : paymentTerms === 'Net 15' ? 15 : paymentTerms === 'Net 45' ? 45 : paymentTerms === 'Net 60' ? 60 : paymentTerms === 'Due on Receipt' ? 0 : 30;
      issue.setDate(issue.getDate() + days);
      setDueDate(issue.toISOString().split('T')[0]);
    }
  }, [issueDate, paymentTerms, dueDate]);

  // Merge in the existing invoice's referenced records when editing, in case the
  // referenced profile/account/application is no longer in the "active" filtered list
  // (or those dropdown queries haven't finished loading yet). Without this, the Select
  // component would show the placeholder and the field would appear blank.
  const profileOptions = useMemo<BusinessProfile[]>(() => {
    const list = profiles || [];
    const existingId = existingInvoice?.businessProfileId as string | undefined;
    if (existingId && !list.some((p) => p.id === existingId)) {
      const existingName = (existingInvoice?.businessProfileName as string | undefined) || 'Current profile';
      return [{ id: existingId, name: existingName }, ...list];
    }
    return list;
  }, [profiles, existingInvoice]);

  const accountOptions = useMemo<AccountLite[]>(() => {
    const list = accounts || [];
    const existingId = existingInvoice?.accountId as string | undefined;
    if (existingId && !list.some((a) => a.id === existingId)) {
      const existingName = (existingInvoice?.accountName as string | undefined) || 'Current client';
      return [{ id: existingId, name: existingName }, ...list];
    }
    return list;
  }, [accounts, existingInvoice]);

  const applicationOptions = useMemo<Application[]>(() => {
    const list = applications || [];
    const existingId = existingInvoice?.applicationId as string | undefined;
    if (existingId && !list.some((a) => a.id === existingId)) {
      const existingName = (existingInvoice?.applicationName as string | undefined) || 'Current application';
      return [{ id: existingId, name: existingName }, ...list];
    }
    return list;
  }, [applications, existingInvoice]);

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
    [items]
  );
  const taxAmount = useMemo(() => Math.round(subtotal * taxRate * 100) / 100, [subtotal, taxRate]);
  const total = subtotal + taxAmount;

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createInvoice(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
      const invoiceData = result.data as { id: string } | undefined;
      if (invoiceData?.id) {
        navigate(`/billing/invoices/${invoiceData.id}`);
      } else {
        navigate('/billing/invoices');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.updateInvoice(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      navigate(`/billing/invoices/${id}`);
    },
  });

  function addItem() {
    setItems([...items, { ...emptyItem, sortOrder: items.length }]);
  }

  function removeItem(index: number) {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  }

  function updateItem(index: number, field: keyof LineItem, value: string | number) {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload = {
      businessProfileId,
      accountId,
      applicationId: applicationId || null,
      issueDate,
      dueDate,
      taxRate,
      notes: notes || null,
      paymentTerms,
      items: items.map((item, i) => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        sortOrder: i,
        itemType: item.itemType,
      })),
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = (createMutation.error || updateMutation.error) as Error | null;

  // Three distinct states when editing: still loading, failed to load, loaded.
  // The failure case used to be indistinguishable from loading, which hid
  // real errors (404s, expired sessions, dropped connections) behind a
  // spinner that never resolved.
  const isLoadingData = isEditing && invoiceLoading;
  const hasLoadFailed = isEditing && !invoiceLoading && (invoiceLoadFailed || !existingInvoice);

  const editHeader = (
    <div className="flex items-center gap-4">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ChevronLeft className="h-4 w-4 mr-1" />
        Back
      </Button>
      <h1 className="text-2xl font-bold">Edit Invoice</h1>
    </div>
  );

  if (isLoadingData) {
    return (
      <div className="space-y-6 max-w-5xl">
        {editHeader}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (hasLoadFailed) {
    const message = (invoiceLoadError as Error | null)?.message
      || 'The invoice could not be loaded.';
    return (
      <div className="space-y-6 max-w-5xl">
        {editHeader}
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Couldn't load this invoice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{message}</p>
            <p className="text-xs text-muted-foreground">
              Invoice ID: <span className="font-mono">{id}</span>
            </p>
            <div className="flex gap-3">
              <Button onClick={() => refetchInvoice()} disabled={invoiceRefetching}>
                <RefreshCw className={cn('h-4 w-4 mr-2', invoiceRefetching && 'animate-spin')} />
                {invoiceRefetching ? 'Retrying...' : 'Try again'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/billing/invoices')}>
                Back to invoices
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing
            ? `Edit Invoice${existingInvoice?.invoiceNumber ? ` #${existingInvoice.invoiceNumber}` : ''}`
            : 'New Invoice'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Header */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Business Profile *</Label>
                <Select value={businessProfileId} onValueChange={setBusinessProfileId} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select billing entity" />
                  </SelectTrigger>
                  <SelectContent>
                    {profileOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Bill To (Client) *</Label>
                <Select value={accountId} onValueChange={setAccountId} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Application</Label>
                <Select
                  value={applicationId}
                  onValueChange={(v) => setApplicationId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {applicationOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                    <SelectItem value="Net 7">Net 7</SelectItem>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxRate * 100}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value || '0') / 100)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Line Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Description</TableHead>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead className="w-[80px] text-right">Qty</TableHead>
                  <TableHead className="w-[120px] text-right">Unit Price</TableHead>
                  <TableHead className="w-[120px] text-right">Amount</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Service description"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.itemType}
                        onValueChange={(v) => updateItem(index, 'itemType', v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="service">Service</SelectItem>
                          <SelectItem value="subscription">Subscription</SelectItem>
                          <SelectItem value="consulting">Consulting</SelectItem>
                          <SelectItem value="product">Product</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value || '0'))}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value || '0'))}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(item.quantity * item.unitPrice)}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} className="text-right font-medium">
                    Subtotal
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(subtotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
                {taxRate > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-medium">
                      Tax ({(taxRate * 100).toFixed(2)}%)
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(taxAmount)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} className="text-right text-lg font-bold">
                    Total
                  </TableCell>
                  <TableCell className="text-right text-lg font-bold">
                    {formatCurrency(total)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes or terms..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        {saveError && (
          <div className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {isEditing ? "Couldn't save your changes" : "Couldn't create the invoice"}
              {saveError.message ? `: ${saveError.message}` : '.'} Nothing was lost — fix the
              problem and press save again.
            </span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving || !businessProfileId || !accountId || !dueDate}>
            {isSaving ? 'Saving...' : isEditing ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </div>
      </form>
    </div>
  );
}
